import React, { useState, useRef, useCallback, useEffect, Dispatch, SetStateAction } from "react";
import styles from "./style.module.scss";
import { ButtonPrimary, CopyElement, Dropdown } from "@components";
import { ReactComponent as SampleIcon } from "../HumanizerInput/assets/svg/Sample.svg";
import { ReactComponent as CopyIcon } from "../HumanizerInput/assets/svg/Copy.svg";
import { Action } from "../HumanizerInput/components";
import { useTranslation } from "react-i18next";
import api from '../../http/axios';

const TONE_OPTIONS = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'formal', label: 'Formal' },
    { value: 'diplomatic', label: 'Diplomatic' },
    { value: 'business', label: 'Business' },
];

const ABSTRACT_COUNT_OPTIONS = [
    { value: 1, label: '1 Abstract' },
    { value: 2, label: '2 Abstracts' },
    { value: 3, label: '3 Abstracts' },
    { value: 4, label: '4 Abstracts' },
    { value: 5, label: '5 Abstracts' },
];

const SAMPLE_TEXT = "This is a sample text for the AI Abstract Generator. Provide a topic or a few keywords, and the AI will generate a abstract for you. For example, you could enter 'the future of renewable energy' or 'the benefits of a healthy lifestyle'.";
const MAX_WORDS = 2000;
const MAX_CHARACTERS = 12000; // Backend limit - roughly 2000 words

const countWords = (str: string): number => {
    str = str.trim();
    if (!str) return 0;
    return str.split(/\s+/).length;
};

export const AbstractGeneratorInput: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
    const { t } = useTranslation();
    
    // --- Constants & Refs for resize functionality ---
    const minHeight = 200;
    const maxHeight = 700;
    const defHeight = 250;
    
    const inputTextareaRef = useRef<HTMLTextAreaElement>(null);
    const outputTextareaRef = useRef<HTMLTextAreaElement>(null);
    const resizeHandleRef = useRef<HTMLDivElement>(null);
    const dragInfoRef = useRef<{ startY: number; startHeight: number } | null>(null);
    
    // --- State Variables ---
    const [inputValue, setInputValue] = useState('');
    const [generatedText, setGeneratedText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [textareaHeight, setTextareaHeight] = useState(defHeight);
    const [isDragging, setIsDragging] = useState(false);
    const [manuallyResized, setManuallyResized] = useState(false);
    const [tone, setTone] = useState<string | number>('professional');
    const [abstractCount, setAbstractCount] = useState<string | number>(1);

    // --- RESIZE HANDLE LOGIC ---
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragInfoRef.current = { startY: e.clientY, startHeight: textareaHeight };
            setIsDragging(true);
            setManuallyResized(true);
        },
        [textareaHeight],
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !dragInfoRef.current) return;
            const deltaY = e.clientY - dragInfoRef.current.startY;
            const newHeight = dragInfoRef.current.startHeight + deltaY;
            const clampedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
            setTextareaHeight(clampedHeight);
            if (inputTextareaRef.current) inputTextareaRef.current.style.height = `${clampedHeight}px`;
            if (outputTextareaRef.current) outputTextareaRef.current.style.height = `${clampedHeight}px`;
        },
        [isDragging, minHeight, maxHeight],
    );

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            dragInfoRef.current = null;
        }
    }, [isDragging]);

    useEffect(() => {
        // Global listeners for drag
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "ns-resize";
            document.body.style.userSelect = "none";
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        }
        return () => {
            // Cleanup
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const handleGenerate = async () => {
        if (!inputValue.trim()) {
            setError('Please enter a topic or keywords to generate a abstract.');
            return;
        }
        
        const wordCount = countWords(inputValue);
        const charCount = inputValue.length;
        
        if (wordCount > MAX_WORDS) {
            setError(`Text is too long. Maximum ${MAX_WORDS} words allowed.`);
            return;
        }
        
        if (charCount > MAX_CHARACTERS) {
            setError(`Text is too long. Maximum ${MAX_CHARACTERS} characters allowed.`);
            return;
        }
        
        setIsLoading(true);
        setError('');
        setGeneratedText('');
        try {
            const response = await api.post('/free-tools/generate-abstract', { 
                text: inputValue,
                tone: tone,
                paragraphs: abstractCount 
            });
            setGeneratedText(response.data.abstract);
        } catch (e: any) {
            if (e.response?.data?.message) {
                // Handle validation errors from backend
                const errorMessage = Array.isArray(e.response.data.message) 
                    ? e.response.data.message.join(', ')
                    : e.response.data.message;
                setError(errorMessage);
            } else {
                setError('An error occurred while generating the abstract.');
            }
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = () => {
        if (generatedText) {
            navigator.clipboard.writeText(generatedText);
        }
    };

    const textAreaChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setError('');
        
        // Auto-resize logic
        if (inputTextareaRef.current && !manuallyResized) {
            inputTextareaRef.current.style.height = "auto";
            const scrollHeight = inputTextareaRef.current.scrollHeight;
            const newHeight = Math.min(Math.max(scrollHeight, minHeight), defHeight);
            inputTextareaRef.current.style.height = `${newHeight}px`;
            setTextareaHeight(newHeight);
            if (outputTextareaRef.current) outputTextareaRef.current.style.height = `${newHeight}px`;
        } else if (outputTextareaRef.current && inputTextareaRef.current) {
            outputTextareaRef.current.style.height = inputTextareaRef.current.style.height;
        }
        if (manuallyResized) {
            setManuallyResized(false);
        }
    };

    const pasteActionClickHandler = async () => {
        try {
            const text = await navigator.clipboard.readText();
            setInputValue(text);
            if (inputTextareaRef.current) {
                inputTextareaRef.current.focus();
                setManuallyResized(false);
                requestAnimationFrame(() => {
                    if (inputTextareaRef.current) {
                        textAreaChangeHandler({
                            target: inputTextareaRef.current,
                        } as React.ChangeEvent<HTMLTextAreaElement>);
                    }
                });
            }
        } catch (err) {
            console.error('Failed to read clipboard contents: ', err);
        }
    };

    const tryASampleClickHandler = () => {
        setInputValue(SAMPLE_TEXT);
        if (inputTextareaRef.current) {
            inputTextareaRef.current.focus();
            setManuallyResized(false);
            requestAnimationFrame(() => {
                if (inputTextareaRef.current) {
                    textAreaChangeHandler({
                        target: inputTextareaRef.current,
                    } as React.ChangeEvent<HTMLTextAreaElement>);
                }
            });
        }
    };

    const renderActions = () => {
        if (inputValue.trim().length > 0) return null;
        
        return (
            <div className={styles.ActionWrapper}>
                <div className={styles.ActionWrapper__container}>
                    <Action
                        text={t('abstractGenerator.actions.trySample')}
                        icon={<SampleIcon />}
                        clickHandler={tryASampleClickHandler}
                    />
                    <Action
                        text={t('abstractGenerator.actions.pasteText')}
                        icon={<CopyIcon />}
                        clickHandler={pasteActionClickHandler}
                    />
                </div>
            </div>
        );
    };

    return (
        <div {...props} className={`${styles.AbstractGeneratorInput} ${props.className || ""}`}>
            <div className={styles.AbstractGeneratorInput__flexWrapper}>
                {/* Input Section */}
                <div className={styles.AbstractGeneratorInput__input}>
                    <div className={styles.AbstractGeneratorInput__topBar}>
                        <h4 className={styles.AbstractGeneratorInput__title}>
                            {t('abstractGenerator.yourText')}
                        </h4>
                        <p className={styles.AbstractGeneratorInput__wordCountInfo}>
                            <span>{countWords(inputValue)}</span> / {MAX_WORDS} words
                        </p>
                    </div>

                    <div className={`${styles.AbstractGeneratorInput__dropdowns} ${styles.customDropdownStyles}`}>
                        <Dropdown
                            label={t('abstractGenerator.dropdown.toneOfVoice')}
                            options={TONE_OPTIONS}
                            defaultValue={tone}
                            onSelect={setTone}
                        />
                        <Dropdown
                            label={t('abstractGenerator.dropdown.numberOfAbstracts')}
                            options={ABSTRACT_COUNT_OPTIONS}
                            defaultValue={abstractCount}
                            onSelect={setAbstractCount}
                        />
                    </div>
                    
                    <div className={styles.AbstractGeneratorInput__fieldWrapper}>
                        <textarea
                            ref={inputTextareaRef}
                            className={styles.AbstractGeneratorInput__field}
                            value={inputValue}
                            onChange={textAreaChangeHandler}
                            placeholder={t('abstractGenerator.inputTextPlaceholder')}
                            style={{
								height: `${textareaHeight}px`,
								minHeight: `${minHeight}px`,
								maxHeight: `${maxHeight}px`,
							}}
                        />
                        {renderActions()}
                    </div>
                    
                    <div className={styles.buttons}>
                        <div className={styles.leftContent}>
                            {/* Empty for now */}
                        </div>
                        <div className={styles.rightContent}>
                             <ButtonPrimary
                                onClick={handleGenerate}
                                disabled={isLoading}
                                colorScheme="orange"
                            >
                                {isLoading ? t('humanizer.processingBtn') : t('abstractGenerator.buttonText')}
                            </ButtonPrimary>
                        </div>
                    </div>
                </div>

                {/* Result Section */}
                <div className={styles.AbstractGeneratorInput__result}>
                    <div className={styles.AbstractGeneratorInput__topBar}>
                        <h4 className={styles.AbstractGeneratorInput__title}>
                            {t('abstractGenerator.generatedText')}
                        </h4>
                        <CopyElement
                             className={`${!generatedText || generatedText.length === 0 ? styles.copyDisabled : ""}`}
                             onClick={handleCopy}
                         />
                    </div>

                    <div className={styles.AbstractGeneratorInput__fieldWrapper}>
                        <textarea
                            ref={outputTextareaRef}
                            className={styles.AbstractGeneratorInput__result_text}
                            value={generatedText}
                            readOnly
                            placeholder={isLoading ? t('humanizer.processing') : t('abstractGenerator.outputTextPlaceholder')}
                            style={{
								height: `${textareaHeight}px`,
								minHeight: `${minHeight}px`,
								maxHeight: `${maxHeight}px`,
							}}
                        />
                    </div>
                     <div className={styles.buttons}>
                        <div className={styles.leftContent}>
                            {/* Empty for now */}
                        </div>
                        <div className={styles.rightContent}>
                           {/* The copy button was moved to the top bar */}
                        </div>
                    </div>
                </div>
            </div>

            {/* RESIZE SLIDER */}
            <div ref={resizeHandleRef} className={styles.resizeHandle} onMouseDown={handleMouseDown} />

            {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
    );
}; 